import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { EphemeralDataSet, getFromColumnRecord, createColumnId } from '../../types';
import { ROW_ID_COLUMN_ID } from '../../types/system-columns';
import { formatDataValue } from '../utils/formatters';
import { useTooltip } from '../hooks/useTooltip';
import * as styles from '../styles/components/data-table.css.ts';

/**
 * DataTableView - Display-only data table component for ephemeral datasets
 * 
 * Features:
 * - Tabular data display with virtual scrolling
 * - Sort functionality (ascending/descending)  
 * - Column resizing
 * - Data type-specific icon display
 * - Virtual scroll rendering (displays only visible rows for performance)
 * 
 * This component is designed specifically for EphemeralDataSet (temporary view-only data).
 * For interactive data tables with row/column selection, use SelectableDataTable.
 * For primary/derived datasets, use SelectableDataTable or other appropriate components.
 */
interface DataTableViewProps {
  dataset: EphemeralDataSet; // 一時的な表示専用データセット
}

export function DataTableView({ dataset }: DataTableViewProps) {

  // 内部状態
  
  // 仮想スクロール用の状態
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(400); // コンテナの高さを状態として管理
  const rowHeight = 41; // DataTableと同じ高さに統一
  // TODO: ブラウザズーム対応 - 実際の行高さを動的に計算してzoom倍率に対応する
  
  // 列幅管理
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // セルツールチップ（500msホバー後に表示）
  const cellTooltip = useTooltip({ offset: { x: 10, y: 10 }, delay: 500 });

  // EphemeralDataSet は常にdataを持つため、特別な処理は不要

  // 表示用のカラム定義を取得（RowNumber列を除外）
  const displayColumns = useMemo(() => {
    return dataset.schema.columns.filter(column => column.id !== ROW_ID_COLUMN_ID);
  }, [dataset]);

  // 全列のデフォルト幅を初期化（ウィンドウ幅に基づいて計算）
  useEffect(() => {
    const newColumnWidths: Record<string, number> = {};
    const hasNewColumns = displayColumns.some(column => {
      const columnKey = column.id || column.name;
      return !(columnKey in columnWidths);
    });
    
    if (hasNewColumns && tableWrapperRef.current) {
      // テーブルコンテナの利用可能幅を取得
      const containerWidth = tableWrapperRef.current.clientWidth;
      const rowNumberColumnWidth = 60; // 行番号列の幅
      const availableWidth = containerWidth - rowNumberColumnWidth;
      
      // 各列に均等に幅を配分（最小幅100px、最大幅300px）
      const equalWidth = Math.max(100, Math.min(300, availableWidth / displayColumns.length));
      
      displayColumns.forEach(column => {
        const columnKey = column.id || column.name;
        if (!(columnKey in columnWidths)) {
          newColumnWidths[columnKey] = equalWidth;
        }
      });
      
      if (Object.keys(newColumnWidths).length > 0) {
        setColumnWidths(prev => ({ ...prev, ...newColumnWidths }));
      }
    }
  }, [displayColumns, columnWidths]);

  // コンテナの高さを監視
  useEffect(() => {
    const updateContainerHeight = () => {
      if (tableWrapperRef.current) {
        const height = tableWrapperRef.current.clientHeight;
        if (height > 0) {
          setContainerHeight(height);
        }
      }
    };

    // 初回計算
    updateContainerHeight();

    // ResizeObserverで監視
    const resizeObserver = new ResizeObserver(updateContainerHeight);
    if (tableWrapperRef.current) {
      resizeObserver.observe(tableWrapperRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // データ取得（EphemeralDataSetは常にdataを持つ）
  const effectiveData = dataset.data;

  // 行インデックスの配列を作成（ソートなし、フィルタなし）
  const rowIndices = useMemo(() => {
    const dataLength = Object.values(effectiveData)[0]?.length || 0;
    return Array.from({ length: dataLength }, (_, i) => i);
  }, [effectiveData]);

  // 仮想スクロールの計算
  const totalRowCount = rowIndices.length;
  const totalContentHeight = totalRowCount * rowHeight;
  
  // 表示領域の計算 - 状態から高さを使用
  const visibleRowsInViewport = Math.ceil(containerHeight / rowHeight);
  const bufferRows = Math.max(5, Math.ceil(visibleRowsInViewport * 0.2));
  
  // 現在のスクロール位置から表示開始行を計算
  const startRowIndex = Math.floor(scrollTop / rowHeight);
  
  // 仮想スクロール：画面に表示される行のみレンダリング
  const visibleStartIndex = Math.max(0, startRowIndex - bufferRows);
  const visibleEndIndex = Math.min(
    startRowIndex + visibleRowsInViewport + bufferRows,
    totalRowCount
  );
  
  const visibleIndices = rowIndices.slice(visibleStartIndex, visibleEndIndex);
  const offsetY = visibleStartIndex * rowHeight;

  // デバッグログ


  // スクロールハンドラー
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
  }, []);


  // レンダリング
  return (
    <div
      className={styles.dataTableContainer}
    >
      <div
        ref={tableWrapperRef}
        className={styles.tableWrapper}
        onScroll={handleScroll}
      >
        {/* 仮想スクロール用の全体高さを持つ要素 */}
        <div
          style={{
            height: totalContentHeight,
            position: 'relative',
            pointerEvents: 'none'
          }}
        >
          {/* 実際のテーブル */}
          <div style={{ position: 'absolute', top: offsetY, width: '100%', pointerEvents: 'auto' }}>
            <table
              className={styles.dataTable}
            >
              <thead>
                <tr>
                  <th
                    className={`${styles.rowNumberHeader} ${styles.stickyHeader}`}
                    style={{ width: 60, minWidth: 60, maxWidth: 60 }}
                  >
                    #
                  </th>
                  {displayColumns.map((column) => {
                    const columnKey = column.id || column.name;
                    const isNumericType = ['int64', 'float64'].includes(column.type);

                    return (
                      <th
                        key={columnKey}
                        className={`${styles.columnHeader} ${styles.stickyHeader} ${isNumericType ? styles.numeric : ''} ${styles.viewMode}`}
                        style={{
                          width: columnWidths[columnKey] || 150,
                          minWidth: 100
                        }}
                        title={`${column.type}`}
                      >
                        <div className={styles.columnHeaderContent}>
                          {/* 1行目: 列名 */}
                          <div className={styles.columnHeaderMainRow}>
                            <span className={styles.columnName}>
                              <span className={styles.editableColumnName}>
                                {column.name}
                              </span>
                            </span>
                          </div>
                          {/* 2行目: データ型 */}
                          <div className={styles.columnHeaderMetaRow}>
                            <span className={styles.columnType}>{column.type}</span>
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visibleIndices.map((dataIndex: number) => (
                  <tr key={dataIndex} className={`${styles.dataRow} ${styles.viewMode}`}>
                    <td
                      className={styles.rowNumber}
                      style={{ width: 60, minWidth: 60, maxWidth: 60 }}
                    >
                      {(() => {
                        // RowNumber列のデータを取得
                        const rowNumberData = getFromColumnRecord(effectiveData, ROW_ID_COLUMN_ID);
                        if (rowNumberData && rowNumberData[dataIndex] != null) {
                          return rowNumberData[dataIndex];
                        }
                        // RowNumber列がない場合はインデックスを表示（フォールバック）
                        return dataIndex + 1;
                      })()}
                    </td>
                    {displayColumns.map((column) => {
                      const columnData = getFromColumnRecord(effectiveData, createColumnId(column.id || column.name));

                      const cellValue = columnData?.[dataIndex];
                      const formattedValue = formatDataValue(cellValue !== undefined ? cellValue : null, column.type);
                      const isNumericType = ['int64', 'float64'].includes(column.type);
                      const columnKey = column.id || column.name;

                      return (
                        <td
                          key={columnKey}
                          className={`${styles.dataCell} ${isNumericType ? styles.numeric : ''} ${styles.viewMode}`}
                          style={{
                            width: columnWidths[columnKey] || 150,
                            minWidth: 100
                          }}
                          onMouseEnter={(e) => cellTooltip.show(formattedValue, e)}
                          onMouseLeave={cellTooltip.hide}
                        >
                          {formattedValue}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* セルツールチップ */}
      <cellTooltip.Tooltip />
    </div>
  );
}